import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useT } from "@/i18n/LanguageContext";
import { printStopLabel } from "@/lib/printStopLabel";
import { canConfirmCancelStop, stopStationForWarehouse, STOP_STATION_LABEL, stopColorLabel } from "@/components/CancelStockStopDialog";

const FLAG = { china: "🇨🇳", usa: "🇺🇸", canada: "🇨🇦" };
export const canAckHoldStop = canConfirmCancelStop;
const SHIP_LABEL_ROLES = ["back_office", "super_admin", "shipping_order", "inventory_packing", "packing_orders"];

/** Client-side mirror of the backend `due` rule for list rows (lists carry the raw order, not ship_hold_stop). */
export const holdStopDue = (o) => {
  if (!o?.ship_hold) return false;
  const st = o.ship_hold_info?.stage;
  if (st === "now") return true;
  if (st === "packing_finish") return !!(o.packed_at || o.inventory_packed_at || o.received_at);
  if (st === "mark_received") return !!o.received_at;
  return false;
};

/** Small amber/red pill for list rows. */
export function HoldChip({ o, testid }) {
  const { t } = useT();
  if (!o?.ship_hold) return null;
  const stored = !!o.ship_hold_info?.stored;
  const due = holdStopDue(o);
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider text-white ${stored ? "bg-amber-600" : due ? "bg-red-600" : "bg-amber-500"}`}
          data-testid={testid || `hold-chip-${o.id}`} title={`${t("ON HOLD")} — ${o.ship_hold_info?.reason || ""} · ${o.ship_hold_info?.by || ""}`}>
      ⏸ {t("ON HOLD")}{stored ? ` · ${t("stored")}` : due ? " · 🛑" : ""}
    </span>
  );
}

/** Full-screen red STOP for an order ON HOLD (rule Jun 2026): the set is stored in the warehouse — never shipped —
 *  until Back Office removes the hold. Station prints the storage label (or a shipping label) and acknowledges. */
export function HoldStopDialog({ orderId, open, onOpenChange, onDone }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { t, lang } = useT();
  const { data: o, isLoading } = useQuery({
    queryKey: ["order", orderId],
    queryFn: async () => (await api.get(`/orders/${orderId}`)).data,
    enabled: !!orderId && !!open,
  });
  const ack = useMutation({
    mutationFn: () => api.post(`/orders/${orderId}/ship-hold/stored`, {}),
    onSuccess: (r) => {
      qc.invalidateQueries();
      toast.success(`⏸ ${o?.po_number} ${t("stored in")} ${t(r?.data?.warehouse_label || "")} — ${t("on hold until Back Office removes it")}`);
      onOpenChange?.(false);
      onDone?.(r?.data);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not acknowledge"),
  });
  if (!open) return null;
  const stop = o?.ship_hold_stop;
  const wrongStation = !!stop && !canAckHoldStop(user, stop.warehouse);
  const whLabel = stop ? t(stop.warehouse_label) : "";
  const colorLabel = stop ? stopColorLabel(stop.color, lang) : "—";
  const existingLabel = ((o?.shipping_label_url || o?.shopify_label_url || "") + "").trim();
  const canShipLabel = SHIP_LABEL_ROLES.includes(user?.role) && !!existingLabel;
  const printLabel = () => {
    if (printStopLabel({ po: o.po_number, cat: stop.cat_number, color: stop.color, warehouse: stop.warehouse, lang, hold: true })) toast.success(t("Label sent to the printer window"));
    else toast.error(t("Popup blocked — allow popups for this site, then print again"));
  };
  return createPortal(
    <div className="fixed inset-0 z-[200] bg-red-700 text-white overflow-y-auto" data-testid="hold-stop" role="alertdialog" aria-modal="true">
      <div className="min-h-full flex flex-col items-center justify-center px-6 py-10 text-center">
        {isLoading || !o ? (
          <div className="text-2xl font-black">{t("Loading…")}</div>
        ) : !stop ? (
          <>
            <div className="text-3xl font-black">{o.po_number} {t("is not on hold")}</div>
            <Button className="mt-6 bg-white text-red-700 hover:bg-red-100" onClick={() => onOpenChange?.(false)} data-testid="hold-stop-close">{t("Close")}</Button>
          </>
        ) : (
          <>
            <div className="text-[96px] leading-none" aria-hidden>🛑</div>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase" data-testid="hold-stop-title">{t("STOP")} · {t("ON HOLD")}</h1>
            <p className="mt-3 text-xl sm:text-2xl font-bold uppercase tracking-wide max-w-3xl" data-testid="hold-stop-headline">
              {o.po_number} {t("is ON HOLD — do NOT ship it. Store it in the")} {FLAG[stop.warehouse] || ""} {whLabel} {t("until Back Office removes the hold.")}
            </p>
            <p className="mt-2 text-base sm:text-lg text-red-100 max-w-2xl" data-testid="hold-stop-reason">
              {t("Reason")}: <b>{stop.reason || "—"}</b> · {t("by")} {stop.by || "—"}{stop.at ? ` · ${new Date(stop.at).toLocaleString()}` : ""}
            </p>
            {!stop.due && (
              <div className="mt-4 rounded-xl bg-yellow-300 text-yellow-950 px-5 py-3 max-w-2xl font-semibold" data-testid="hold-stop-not-due">
                {t("The STOP applies")} {t(stop.when)} — {t("until then the order continues normally.")}
              </div>
            )}

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
              <div className="rounded-2xl bg-white text-slate-900 p-6">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">{t("Catalog number")}</div>
                <div className="mt-1 text-3xl sm:text-4xl font-black font-mono break-words" data-testid="hold-stop-cat">{stop.cat_number || "— MISSING —"}</div>
              </div>
              <div className="rounded-2xl bg-white text-slate-900 p-6">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">{t("Colour")}</div>
                <div className="mt-1 text-3xl sm:text-4xl font-black" data-testid="hold-stop-color">{colorLabel}</div>
              </div>
            </div>

            {!wrongStation && (
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <Button className="h-auto min-h-12 py-2 px-6 text-base font-black bg-white text-slate-900 hover:bg-slate-100 whitespace-normal max-w-full" onClick={printLabel} data-testid="hold-stop-print-label">
                  🖨 {t("Print storage label")} · {stop.cat_number || "—"} · {colorLabel}
                </Button>
                {canShipLabel && (
                  <Button className="h-auto min-h-12 py-2 px-6 text-base font-black bg-red-900 text-white hover:bg-red-950 border border-red-300 whitespace-normal max-w-full" onClick={() => window.open(existingLabel, "_blank", "noopener")} data-testid="hold-stop-print-shipping-label">
                    🖨 {t("Reprint shipping label")} · {t("stick it on the stored box")}
                  </Button>
                )}
              </div>
            )}

            {wrongStation ? (
              <div className="mt-8 rounded-xl bg-yellow-300 text-yellow-950 px-5 py-4 max-w-2xl font-semibold" data-testid="hold-stop-wrong-station">
                {t("This set is in the")} {FLAG[stop.warehouse] || ""} {whLabel} — <b>{t(STOP_STATION_LABEL[stopStationForWarehouse(stop.warehouse)])}</b> {t("stores it, not your station.")}
              </div>
            ) : stop.stored ? (
              <div className="mt-8 rounded-xl bg-emerald-500 text-white px-5 py-4 max-w-2xl font-semibold" data-testid="hold-stop-stored">
                ✓ {t("Stored in")} {whLabel} {t("by")} {stop.stored.by}{stop.stored.at ? ` · ${new Date(stop.stored.at).toLocaleString()}` : ""} — {t("waiting for Back Office to remove the hold")}
              </div>
            ) : null}

            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              {!stop.stored && (
                <Button
                  className="h-auto min-h-14 py-3 px-8 text-lg font-black bg-white text-red-700 hover:bg-red-100 disabled:opacity-40 whitespace-normal max-w-full"
                  disabled={wrongStation || !stop.due || ack.isPending}
                  onClick={() => ack.mutate()}
                  data-testid="hold-stop-confirm"
                >
                  {ack.isPending ? t("Saving…") : `✓ ${t("Stored on the")} ${whLabel} ${t("shelf")}`}
                </Button>
              )}
              <Button variant="ghost" className="h-14 px-6 text-white hover:bg-red-800" onClick={() => onOpenChange?.(false)} data-testid="hold-stop-later">
                {stop.stored ? t("Close") : t("Later")}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

/** Small red button that opens the hold STOP (lists, dashboard, order page). */
export function HoldStopButton({ orderId, due = true, stored = false, size = "sm", className = "", onDone }) {
  const [open, setOpen] = useState(false);
  const { t } = useT();
  return (
    <>
      <Button
        size={size}
        className={`${stored ? "bg-amber-600 hover:bg-amber-700" : "bg-red-600 hover:bg-red-700"} text-white font-black ${className}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        data-testid={`hold-stop-open-${orderId}`}
        title={stored ? t("On hold — stored in the warehouse") : due ? t("On hold — store the set, do not ship") : t("STOP appears after packing / receiving")}
      >
        🛑 {stored ? t("ON HOLD · stored") : due ? t("STOP — on hold") : t("ON HOLD")}
      </Button>
      <HoldStopDialog orderId={orderId} open={open} onOpenChange={setOpen} onDone={onDone} />
    </>
  );
}
